"use client";

import React, { useState, useEffect, useCallback, useRef } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import Link from "next/link";
import { getPostImageUrl } from "@/components/ui/PostImage";
import { VideoPlayIndicator } from "@/components/ui/VideoPlayIndicator";
import { useNavigation } from "@/lib/navigation/NavigationProvider";
import type { Post, Category } from "@/payload-types";

export interface ArticleCarouselProps {
    articles: Post[];
    /** Auto-advance interval in ms. 0 to disable. Default: 6000 */
    interval?: number;
}

/**
 * ArticleCarousel — Hero article slider with smooth CSS slide transition,
 * arrow controls and dot indicators below the title (Serie A style).
 * Only the image slides; the text overlay stays fixed at the bottom.
 */
export const ArticleCarousel: React.FC<ArticleCarouselProps> = ({ articles, interval = 6000 }) => {
    const nav = useNavigation();
    const [current, setCurrent] = useState(0);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [paused, setPaused] = useState(false);

    const count = articles.length;

    const goTo = useCallback(
        (index: number) => {
            if (isTransitioning) return;
            const target = ((index % count) + count) % count;
            if (target === current) return;
            setIsTransitioning(true);
            setCurrent(target);
        },
        [count, current, isTransitioning]
    );

    const goPrev = useCallback(() => goTo(current - 1), [goTo, current]);
    const goNext = useCallback(() => goTo(current + 1), [goTo, current]);

    const handleTransitionEnd = useCallback(() => setIsTransitioning(false), []);

    // Auto-advance
    useEffect(() => {
        if (paused || interval === 0 || count <= 1) return;
        const timer = setInterval(() => goTo(current + 1), interval);
        return () => clearInterval(timer);
    }, [current, paused, interval, count, goTo]);

    // Touch/swipe
    const touchStartRef = useRef<number | null>(null);

    const handleTouchStart = (e: React.TouchEvent) => {
        touchStartRef.current = e.touches[0].clientX;
        setPaused(true);
    };

    const handleTouchEnd = (e: React.TouchEvent) => {
        if (touchStartRef.current === null) return;
        const diff = e.changedTouches[0].clientX - touchStartRef.current;
        if (Math.abs(diff) > 50) {
            if (diff < 0) goNext();
            else goPrev();
        }
        touchStartRef.current = null;
        setPaused(false);
    };

    if (count === 0) return null;

    const currentArticle = articles[current];

    // Get first populated category for the current article
    const category = currentArticle.categories?.find((c): c is Category => typeof c !== "number" && !!c);

    return (
        <div
            className="relative w-full aspect-video rounded-lg overflow-hidden"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
        >
            {/* Layer 1: Sliding image track */}
            <div
                className="absolute inset-0 flex transition-transform duration-500 ease-in-out"
                style={{ transform: `translateX(-${current * 100}%)` }}
                onTransitionEnd={handleTransitionEnd}
            >
                {articles.map((article) => (
                    <Link
                        prefetch={false}
                        key={article.id}
                        href={nav.post(article)}
                        className="relative block shrink-0 w-full h-full cursor-pointer"
                        style={{ minWidth: "100%" }}
                    >
                        <img
                            src={getPostImageUrl(article, "xlarge")}
                            alt={article.title}
                            className="w-full h-full object-cover"
                        />
                        {article.type === "video" && <VideoPlayIndicator size="lg" />}
                    </Link>
                ))}
            </div>

            {/* Layer 2: Fixed gradient overlay */}
            <div
                className="absolute inset-0 pointer-events-none"
                style={{
                    background: "linear-gradient(to top, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.5) 30%, transparent 60%)",
                }}
            />

            {/* Layer 3: Title + controls (fixed, not sliding) */}
            <div className="absolute bottom-0 left-0 right-0 z-10 p-5 md:p-8">
                {/* Category badge */}
                {category && (
                    <span className="inline-block px-3 py-1 mb-3 text-xs font-bold uppercase tracking-wider bg-secondary text-secondary-foreground rounded">
                        {category.title}
                    </span>
                )}

                {/* Title — links to current article */}
                <Link prefetch={false} href={nav.post(currentArticle)} className="block mb-3 cursor-pointer">
                    <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-white line-clamp-3">
                        {currentArticle.title}
                    </h2>
                </Link>

                {/* Controls: arrows + dots */}
                {count > 1 && (
                    <div className="flex items-center gap-2">
                        <button
                            onClick={goPrev}
                            className="flex items-center justify-center w-7 h-7 rounded-full bg-white/20 backdrop-blur-sm text-white hover:bg-white/40 transition-colors cursor-pointer"
                            aria-label="Previous slide"
                        >
                            <ChevronLeft className="w-4 h-4" />
                        </button>
                        <button
                            onClick={goNext}
                            className="flex items-center justify-center w-7 h-7 rounded-full bg-white/20 backdrop-blur-sm text-white hover:bg-white/40 transition-colors cursor-pointer"
                            aria-label="Next slide"
                        >
                            <ChevronRight className="w-4 h-4" />
                        </button>

                        <div className="flex items-center gap-1.5 ml-1">
                            {articles.map((_, i) => (
                                <button
                                    key={i}
                                    onClick={() => goTo(i)}
                                    className={`block rounded-full transition-all duration-300 cursor-pointer ${
                                        i === current
                                            ? "w-6 h-2.5 bg-white/60"
                                            : "w-2.5 h-2.5 bg-white/40 hover:bg-white/70"
                                    }`}
                                    aria-label={`Go to slide ${i + 1}`}
                                />
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
